/**
 * Context API service.
 *
 * Centralizes all context-related API calls with consistent error handling
 * and TypeScript types.
 *
 * @module contextApi
 */

import { apiGet, apiPost, apiPut, apiDelete } from '@/lib/apiUtils';

/**
 * Context interface matching backend structure
 */
export interface Context {
  id: number;
  name: string;
  type: 'text' | 'image' | 'audio';
  content?: string;
  fileExtension?: string;
  mimeType?: string;
  binaryContent?: string;
  mediaUrl?: string | null;
  transcript?: string;
  generationModel?: string | null;
  createdAt: string;
  updatedAt: string;
  textPreview?: string;
  binarySize?: number;
  hasFile?: boolean;
  levels?: string[];
  modules?: string[];
  sections?: number[];
  examTypes?: string[];
}

/**
 * Context row from the admin paginated contexts list endpoint.
 */
export interface AdminContextListItem {
  id: string;
  name: string;
  type: string;
  content?: string;
  fileName?: string;
  mimeType?: string;
  transcript?: string;
  generationModel?: string | null;
  createdAt: string;
  updatedAt?: string;
  levels?: string[];
  modules?: string[];
  sections?: number[];
  examTypes?: string[];
}

/**
 * Context usage information
 */
export interface ContextUsageQuestion {
  id: number;
  question_text: string;
  type?: string;
  level?: string;
}

export interface ContextUsage {
  questionCount: number;
  questions: ContextUsageQuestion[];
}

export interface ContextUsageSummary extends ContextUsage {
  contextId: number | string;
  contextName: string | null;
}

/**
 * Context data for create/update operations
 */
export interface ContextFormData {
  name: string;
  type: 'text' | 'image' | 'audio';
  content?: string;
  fileName?: string;
  mimeType?: string;
  transcript?: string;
  confirmSave?: boolean;
}

/**
 * Similar context information
 */
export interface SimilarContext {
  id: string;
  name: string;
  context_type: string;
  content: string;
  similarity: number;
}

/**
 * Confirmation required response (when similar contexts found)
 */
export interface ConfirmationRequiredResponse {
  requiresConfirmation: true;
  message: string;
  similarContexts: SimilarContext[];
  maxSimilarity: number;
  threshold: number;
}

/**
 * Custom error for confirmation-required responses
 */
export class ConfirmationRequiredError extends Error {
  requiresConfirmation: true;
  similarContexts: SimilarContext[];
  maxSimilarity: number;
  threshold: number;
  message: string;

  constructor(data: ConfirmationRequiredResponse) {
    super(data.message);
    this.name = 'ConfirmationRequiredError';
    this.requiresConfirmation = true;
    this.similarContexts = data.similarContexts;
    this.maxSimilarity = data.maxSimilarity;
    this.threshold = data.threshold;
    this.message = data.message;
  }
}

type ContextMutationResponse = Context | ConfirmationRequiredResponse;

function assertContextMutationResult(
  responseData: ContextMutationResponse | null,
): Context {
  if (!responseData) {
    throw new Error('Failed to save context');
  }
  if (
    'requiresConfirmation' in responseData &&
    responseData.requiresConfirmation === true
  ) {
    throw new ConfirmationRequiredError(responseData);
  }
  return responseData;
}

/**
 * Get lightweight list of contexts with previews and metadata
 *
 * @returns Promise resolving to array of context objects
 * @throws Error if API request fails
 */
export const getContextsList = async (): Promise<Context[]> => {
  const result = await apiGet<Context[]>('/contexts/list', {
    errorMessage: 'Failed to fetch contexts list',
    context: 'getContextsList',
    throwOnError: true,
  });
  return result ?? [];
};

/**
 * Get single context with full text content and transcript
 *
 * @param id - Context ID
 * @returns Promise resolving to context object
 * @throws Error if API request fails
 */
export const getContextById = async (id: number): Promise<Context> => {
  const result = await apiGet<Context>(`/contexts/${id}/full`, {
    errorMessage: 'Failed to fetch context',
    context: 'getContextById',
    throwOnError: true,
  });
  if (!result) {
    throw new Error('Failed to fetch context');
  }
  return result;
};

/**
 * Create a new context
 *
 * @param contextData - Context data to create
 * @returns Promise resolving to created context object
 * @throws ConfirmationRequiredError if similar contexts found
 * @throws Error if API request fails
 */
export const createContext = async (
  contextData: ContextFormData,
): Promise<Context> => {
  const responseData = await apiPost<ContextMutationResponse>(
    '/contexts',
    contextData,
    {
      errorMessage: 'Failed to create context',
      context: 'createContext',
      throwOnError: true,
    },
  );
  return assertContextMutationResult(responseData);
};

/**
 * Update an existing context
 *
 * @param id - Context ID to update
 * @param contextData - Updated context data
 * @returns Promise resolving to updated context object
 * @throws ConfirmationRequiredError if similar contexts found
 * @throws Error if API request fails
 */
export const updateContext = async (
  id: number,
  contextData: ContextFormData,
): Promise<Context> => {
  const responseData = await apiPut<ContextMutationResponse>(
    `/contexts/${id}`,
    contextData,
    {
      errorMessage: 'Failed to update context',
      context: 'updateContext',
      throwOnError: true,
    },
  );
  return assertContextMutationResult(responseData);
};

/**
 * Delete a context by ID
 *
 * @param id - Context ID to delete
 * @returns Promise resolving on successful deletion
 * @throws Error if API request fails
 */
export const deleteContext = async (id: number): Promise<void> => {
  await apiDelete(`/contexts/${id}`, {
    errorMessage: 'Failed to delete context',
    context: 'deleteContext',
    throwOnError: true,
  });
};

/**
 * Get usage information for a context
 *
 * @param id - Context ID
 * @returns Promise resolving to usage information
 */
export const getContextUsage = async (id: number): Promise<ContextUsage> => {
  const result = await apiGet<ContextUsage>(`/contexts/${id}/usage`, {
    errorMessage: 'Failed to check context usage',
    context: 'getContextUsage',
    errorReturnValue: { questionCount: 0, questions: [] },
  });
  return result ?? { questionCount: 0, questions: [] };
};

/**
 * Bulk fetch usage information for multiple contexts
 *
 * @param ids - Context IDs
 * @returns Promise resolving to array of usage summaries per context
 */
export const getBulkContextUsage = async (
  ids: number[],
): Promise<ContextUsageSummary[]> => {
  const data = await apiPost<{ usages?: ContextUsageSummary[] }>(
    '/contexts/usage/bulk',
    { ids },
    {
      errorMessage: 'Failed to fetch bulk context usage',
      context: 'getBulkContextUsage',
      throwOnError: true,
    },
  );
  return data?.usages ?? [];
};

/**
 * Bulk delete multiple contexts by IDs
 *
 * @param ids - Array of context IDs to delete
 * @returns Promise resolving on successful deletion
 * @throws Error if API request fails
 */
export const bulkDeleteContexts = async (ids: number[]): Promise<void> => {
  await apiDelete('/contexts/bulk', {
    body: JSON.stringify({ ids }),
    errorMessage: 'Failed to bulk delete contexts',
    context: 'bulkDeleteContexts',
    throwOnError: true,
  });
};

/**
 * Fetch binary content for a context (lazy loading)
 *
 * @param contextId - Context ID
 * @returns Promise resolving to base64-encoded binary content or null
 */
export const fetchContextBinaryContent = async (
  contextId: number,
): Promise<string | null> => {
  const data = await apiGet<{ binary_content_base64?: string }>(
    `/admin/context-binary/${contextId}`,
    {
      errorMessage: `Failed to fetch binary content for context ${contextId}`,
      context: 'fetchContextBinaryContent',
      errorReturnValue: null,
    },
  );
  return data?.binary_content_base64 ?? null;
};
